import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { useCountries, useLocalUserPreference, useMyCountryFromIp } from '@percent/cause-dashboard/common/hooks'
import { Button, ButtonText, FormField, Grid, Select, TextInput, Typography } from '@percent/lemonade'
import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import styles from './ClaimSearchHeaderForm.module.scss'
import { ClaimSearchHeaderFormProps } from './ClaimSearchHeaderForm.types'

const MIN_SEARCH_LENGTH = 3

export function ClaimSearchHeaderForm({ onSearchClick }: ClaimSearchHeaderFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [disableSearchButton, setDisableSearchButton] = useState(false)
  const countries = useCountries()
  const countryCodes = countries.map(({ name, code }: { name: string; code: string }) => ({
    label: name,
    value: code
  }))

  const { country: myCountry } = useMyCountryFromIp({
    useLocalCache: true
  })

  const [userPreferenceLocalCountry, setUserPreferenceLocalCountry] = useLocalUserPreference('country')

  const { errors, values, setFieldValue, handleSubmit, handleBlur } = useFormik({
    enableReinitialize: false,
    initialValues: {
      countryCode: userPreferenceLocalCountry ?? myCountry ?? '',
      query: ''
    },
    validationSchema: Yup.object().shape({
      countryCode: Yup.string().max(6, 'Must be 6 characters or less').required('Required'),
      query: Yup.string()
        .min(MIN_SEARCH_LENGTH, `Must be at least ${MIN_SEARCH_LENGTH} characters`)
        .max(500, 'Must be fewer than 500 characters')
    }),
    onSubmit: ({ countryCode, query }) => {
      setUserPreferenceLocalCountry(countryCode, { expiresInHours: 24 * 7 })
      onSearchClick(countryCode, query)
    }
  })

  useEffect(() => {
    if (!userPreferenceLocalCountry) {
      return
    }
    setFieldValue('countryCode', userPreferenceLocalCountry ?? myCountry ?? '')
  }, [myCountry, setFieldValue, userPreferenceLocalCountry])

  const searchInputRef = React.createRef<HTMLInputElement>()

  useEffect(() => {
    searchInputRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (errors.query) {
      setDisableSearchButton(true)
    } else {
      setDisableSearchButton(false)
    }
  }, [errors])

  return (
    <form onSubmit={handleSubmit}>
      <Grid className={styles.mainGrid} w="100%" gutter={12}>
        <Grid.Col span={{ xs: 12, md: 5 }}>
          <FormField label={t('typography.selectCountry')}>
            <Select
              options={countryCodes}
              defaultValue={countryCodes.find(country => country.value === values.countryCode)}
              onChange={event => {
                setFieldValue('countryCode', event.value)
              }}
              data-testid="claim-country-select"
              searchable
            />
          </FormField>
        </Grid.Col>

        <Grid.Col span={{ xs: 7, md: 5 }}>
          <FormField
            label={t('typography.organizationRegisteredNumber')}
            statusMessage={errors.query}
            data-testid="search-header-search-input"
          >
            <TextInput
              name="query"
              value={values.query}
              onChange={a => setFieldValue('query', a.target.value)}
              onBlur={handleBlur}
              ref={searchInputRef}
              placeholder={t('typography.organizationNamePlaceholder')}
            />
          </FormField>
          {errors.query && (
            <Typography style={{ paddingTop: 13 }}>
              {t('typography.minSearchLengthMessage', { count: MIN_SEARCH_LENGTH })}
            </Typography>
          )}
        </Grid.Col>

        <Grid.Col
          span={{ xs: 5, md: 2 }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: errors.query ? 'center' : 'end'
          }}
        >
          <Button stretch disabled={disableSearchButton} data-testid="search-header-search-button" type="submit">
            {t('button.search')}
          </Button>
        </Grid.Col>

        <Grid.Col className={`${styles.linkContainer} ${errors.query ? styles.linkContainerWithError : ''}`}>
          <ButtonText onClick={() => navigate(RoutePath.ADD_ORGANIZATION)} data-testid="cant-find-org-button">
            {t('typography.cantFindMyOrganization')}
          </ButtonText>
        </Grid.Col>
      </Grid>
    </form>
  )
}
